import React from 'react';
import useSWR from 'swr';
import { mutate as globalMutate } from 'swr';
import api from '../../../services/api';
import { fetcher } from '../../../services/fetcher';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

const API_LIST = '/management-companies';
const apiDetail = (id: number) => `/management-companies/${id}`;

export interface ManagementCompany {
  id: number;
  code: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  notes?: string | null;
  logoStorageKey?: string | null;
  logoOriginalFilename?: string | null;
  logoMimeType?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const useManagementCompanies = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<ManagementCompany> | ManagementCompany[]
  >(`${API_LIST}${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<ManagementCompany>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useManagementCompany = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<ManagementCompany>(
    id ? apiDetail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

export function useManagementCompanyLogoBlobUrl(
  companyId: number | null | undefined,
  logoStorageKey: string | null | undefined,
) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const blobUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!companyId || !logoStorageKey) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/management-companies/${companyId}/logo`, {
        responseType: 'blob',
        headers: { Accept: 'image/*,application/octet-stream' },
      })
      .then((res) => {
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const blob = res.data as Blob;
        if (res.status === 204 || !blob || blob.size === 0) {
          setBlobUrl(null);
          return;
        }
        // Axios may hand back a JSON error as a Blob when responseType is blob.
        const type = (blob.type || '').toLowerCase();
        if (type.includes('json') || type.includes('text/')) {
          setBlobUrl(null);
          return;
        }
        const imageBlob =
          type.startsWith('image/') || type === 'application/octet-stream' || !type
            ? new Blob([blob], { type: type.startsWith('image/') ? type : 'image/png' })
            : blob;
        const url = URL.createObjectURL(imageBlob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [companyId, logoStorageKey]);

  return { logoBlobUrl: blobUrl, loading };
}

export async function uploadManagementCompanyLogo(id: number, file: File) {
  const formData = new FormData();
  // Keep the original filename so the server can detect .png mime/extension.
  formData.append('file', file, file.name || 'logo.png');
  // Do not set Content-Type manually — the browser must add the multipart boundary.
  const res = await api.post(`/management-companies/${id}/logo`, formData);
  await globalMutate(`/management-companies/${id}`);
  await globalMutate((key) => typeof key === 'string' && key.startsWith('/management-companies'));
  return res.data as ManagementCompany;
}

export async function deleteManagementCompanyLogo(id: number) {
  const res = await api.delete(`/management-companies/${id}/logo`);
  await globalMutate(`/management-companies/${id}`);
  await globalMutate((key) => typeof key === 'string' && key.startsWith('/management-companies'));
  return res.data as ManagementCompany;
}
